import React from "react";
import { Helmet } from "react-helmet-async";
import { useI18n } from "@/contexts/I18nContext";

const SITE = "Akomoda";
const SITE_URL = "https://akomoda.co";

const DEFAULTS = {
  sq: {
    title: "Akomoda — Program hotelerie për hotele dhe apartamente",
    description:
      "Akomoda është program hotelerie për hotele, resorte dhe apartamente: rezervimet, recepsioni, booking engine, channel manager dhe pastrimi — të gjitha në një sistem modern në gjuhën shqipe.",
    ogLocale: "sq_AL",
  },
  en: {
    title: "Akomoda — Hotel management software for hotels & apartments",
    description:
      "Akomoda is professional hotel management software for hotels, resorts and short-stay apartments: reservations, front desk, booking engine, channel manager and housekeeping in one modern platform.",
    ogLocale: "en_US",
  },
};

const DEFAULT_OG = "/akomoda-logo-dark.png";

/**
 * Given a browser pathname, return {path, lang} where `path` is the
 * language-neutral canonical path (always starts with "/").
 * Example: "/en/pms" → { path: "/pms", lang: "en" }
 *          "/cmimet" → { path: "/cmimet", lang: "sq" }
 */
function splitLangPath(pathname) {
  const m = /^\/(en)(\/.*)?$/.exec(pathname || "/");
  if (m) return { lang: "en", path: m[2] || "/" };
  return { lang: "sq", path: pathname || "/" };
}

function buildUrl(lang, path) {
  if (lang === "sq") return `${SITE_URL}${path === "/" ? "/" : path}`;
  return `${SITE_URL}/en${path === "/" ? "" : path}`;
}

export default function SEO({
  title,
  description,
  canonical,
  ogImage,
  schema,
  noindex = false,
  lang: forcedLang,
}) {
  const { lang: ctxLang } = useI18n() || {};
  const pathname = typeof window !== "undefined" ? window.location.pathname : "/";
  const { lang: urlLang, path } = splitLangPath(pathname);
  const lang = forcedLang || urlLang || ctxLang || "sq";

  const defaults = DEFAULTS[lang] || DEFAULTS.sq;
  // Avoid a duplicate "| Akomoda" when the caller already included the brand
  // in the passed-in title (common for blog post meta_title values).
  const cleanTitle = title ? title.replace(/\s*[|·]\s*Akomoda\s*$/i, "").trim() : "";
  const fullTitle = cleanTitle ? `${cleanTitle} | ${SITE}` : defaults.title;
  const desc = description || defaults.description;
  const canonicalUrl = canonical || buildUrl(lang, path);
  const image = ogImage || DEFAULT_OG;

  // hreflang alternates for the current path in every supported language
  const alternates = [
    { hreflang: "sq", href: buildUrl("sq", path) },
    { hreflang: "en", href: buildUrl("en", path) },
    { hreflang: "x-default", href: buildUrl("sq", path) },
  ];

  const customSchemas = schema ? (Array.isArray(schema) ? schema : [schema]) : [];

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={canonicalUrl} />
      {alternates.map((a) => (
        <link key={a.hreflang} rel="alternate" hrefLang={a.hreflang} href={a.href} />
      ))}
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={defaults.ogLocale} />
      <meta property="og:site_name" content={SITE} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />
      {customSchemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}
